import { getStatusesByCategory, StatusConfig } from '@/services/status.service';

// Cache for statuses to avoid repeated API calls
let projectStatusesCache: StatusConfig[] | null = null;
let taskStatusesCache: StatusConfig[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper function to convert hex color to Tailwind classes
// Also handles colors without # prefix and returns both classes and hex for inline styles
export const getColorClasses = (color: string): string => {
  // Normalize color (add # if missing, uppercase)
  const normalizedColor = color.startsWith('#') ? color.toUpperCase() : `#${color.toUpperCase()}`;
  
  // Map common hex colors to Tailwind classes
  const colorMap: Record<string, string> = {
    '#6B7280': 'bg-gray-100 text-gray-800 border-gray-200',
    '#3B82F6': 'bg-blue-100 text-blue-800 border-blue-200',
    '#8B5CF6': 'bg-purple-100 text-purple-800 border-purple-200',
    '#F59E0B': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    '#10B981': 'bg-green-100 text-green-800 border-green-200',
    '#EF4444': 'bg-red-100 text-red-800 border-red-200',
    '#06B6D4': 'bg-cyan-100 text-cyan-800 border-cyan-200',
    '#059669': 'bg-emerald-100 text-emerald-800 border-emerald-200',
    // Add more common colors
    '#F97316': 'bg-orange-100 text-orange-800 border-orange-200',
    '#EC4899': 'bg-pink-100 text-pink-800 border-pink-200',
    '#6366F1': 'bg-indigo-100 text-indigo-800 border-indigo-200',
    '#14B8A6': 'bg-teal-100 text-teal-800 border-teal-200',
    '#84CC16': 'bg-lime-100 text-lime-800 border-lime-200',
  };
  
  return colorMap[normalizedColor] || '';
};

// Helper to get hex color with proper format
export const normalizeHexColor = (color: string): string => {
  if (!color) return '#6B7280'; // Default gray
  return color.startsWith('#') ? color : `#${color}`;
};

// Helper to convert hex to RGB for opacity
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const normalized = normalizeHexColor(hex);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalized);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Get project status color
export const getProjectStatusColor = async (statusName: string): Promise<string> => {
  try {
    // Use cache if available and fresh
    if (!projectStatusesCache || Date.now() - cacheTimestamp > CACHE_DURATION) {
      projectStatusesCache = await getStatusesByCategory('project');
      cacheTimestamp = Date.now();
    }
    
    const status = projectStatusesCache.find(s => 
      s.name === statusName || 
      s.name.toLowerCase() === statusName.toLowerCase() ||
      statusName.toLowerCase().includes(s.name.toLowerCase())
    );
    
    if (status) {
      return getColorClasses(status.color);
    }
  } catch (error) {
    console.error('Error fetching project status color:', error);
  }
  
  // Fallback colors
  const fallbackColors: Record<string, string> = {
    'Planning': 'bg-gray-100 text-gray-800 border-gray-200',
    'In Progress': 'bg-blue-100 text-blue-800 border-blue-200',
    'On Hold': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Completed': 'bg-green-100 text-green-800 border-green-200',
  };
  
  return fallbackColors[statusName] || 'bg-gray-100 text-gray-800 border-gray-200';
};

// Get task status color
export const getTaskStatusColor = async (statusName: string): Promise<string> => {
  try {
    // Use cache if available and fresh
    if (!taskStatusesCache || Date.now() - cacheTimestamp > CACHE_DURATION) {
      taskStatusesCache = await getStatusesByCategory('task');
      cacheTimestamp = Date.now();
    }
    
    const status = taskStatusesCache.find(s => 
      s.name === statusName || 
      s.name.toLowerCase() === statusName.toLowerCase() ||
      statusName.toLowerCase().includes(s.name.toLowerCase())
    );
    
    if (status) {
      return getColorClasses(status.color);
    }
  } catch (error) {
    console.error('Error fetching task status color:', error);
  }
  
  // Fallback colors
  const fallbackColors: Record<string, string> = {
    'Not Started': 'bg-gray-100 text-gray-800 border-gray-200',
    'In Progress': 'bg-blue-100 text-blue-800 border-blue-200',
    'Testing': 'bg-purple-100 text-purple-800 border-purple-200',
    'Awaiting Feedback': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Ready for Production': 'bg-green-100 text-green-800 border-green-200',
    'On Hold': 'bg-red-100 text-red-800 border-red-200',
    'Complete': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  };
  
  return fallbackColors[statusName] || 'bg-gray-100 text-gray-800 border-gray-200';
};

// Clear cache (useful when statuses are updated)
export const clearStatusCache = () => {
  projectStatusesCache = null;
  taskStatusesCache = null;
  cacheTimestamp = 0;
};

